import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { getInvitation, acceptInvitationStep1 } from "../lib/api";
import WhatsAppVerifyStep from "../components/WhatsAppVerifyStep.jsx";
import PasswordStrength from "../components/PasswordStrength.jsx";
import { Icon } from "../components/Common.jsx";
import { meetsMinStrength, passwordGateError } from "../lib/password-utils";

// Mask a phone like "+919876543210" → "+91 *****43210" so we don't echo
// the full number back to whoever happens to be looking at the screen.
const maskPhone = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 6) return raw;
  const last4 = digits.slice(-4);
  const cc = digits.length > 10 ? digits.slice(0, digits.length - 10) : "";
  return `${cc ? "+" + cc + " " : ""}******${last4}`;
};

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  // status: 'loading' | 'ready' | 'verify' | 'done' | 'error'
  const [status, setStatus] = useState("loading");
  const [invitation, setInvitation] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // step 1 state
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // verify state (filled after step1)
  const [verifyInfo, setVerifyInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("Missing invitation token.");
      return;
    }
    getInvitation(token)
      .then((inv) => {
        setInvitation(inv);
        setStatus("ready");
      })
      .catch((err) => {
        setStatus("error");
        if (err.message === "invitation_not_found")
          setErrorMsg("Invitation not found.");
        else if (err.message === "invitation_already_accepted")
          setErrorMsg("This invitation has already been used.");
        else if (err.message === "invitation_expired")
          setErrorMsg("This invitation has expired.");
        else setErrorMsg("Could not load invitation. Please try again.");
      });
  }, [token]);

  // ── Step 1: name + password → triggers backend SMS OTP ────────────────
  const handleStep1 = async (ev) => {
    ev.preventDefault();
    const errs = {};
    if (!name.trim()) errs.name = "Name is required";
    if (!meetsMinStrength(password)) errs.password = passwordGateError(password);
    if (password !== confirm) errs.confirm = "Passwords do not match";
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    setErrorMsg("");
    try {
      const data = await acceptInvitationStep1({
        token,
        name: name.trim(),
        password,
      });
      setVerifyInfo(data);
      setStatus("verify");
    } catch (err) {
      if (err.message === "invitation_locked") {
        setErrorMsg(
          "This invitation has been locked after too many failed attempts. Ask your manager to send a new one.",
        );
      } else if (err.message === "phone_missing") {
        setErrorMsg(
          "This invitation has no phone number. Ask your manager to re-invite you with a phone.",
        );
      } else {
        setErrorMsg(err.message || "Could not start verification.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 2: webhook fires when user sends "verify <token>" in WhatsApp.
  // WhatsAppVerifyStep polls /verify-status and calls this on success.
  const handleVerified = () => {
    setStatus("done");
    setTimeout(
      () => navigate("/signin?registered=1", { replace: true }),
      1800,
    );
  };

  // ── Layout primitives ─────────────────────────────────────────────────
  const wrap = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg, #0a0a14)",
    padding: 20,
  };
  const card = {
    width: "100%",
    maxWidth: 440,
    background: "var(--surface, #14141C)",
    border: "1px solid var(--border, #262636)",
    borderRadius: 12,
    padding: 28,
    color: "var(--text, #F1F5F9)",
  };
  const inputStyle = (err) => ({
    width: "100%",
    background: "var(--surface-2)",
    border: `1px solid ${err ? "var(--error)" : "var(--border)"}`,
    borderRadius: 8,
    padding: "10px 12px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
  });
  const eyeBtnStyle = {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "var(--text-muted)",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
  };

  if (status === "loading")
    return (
      <div style={wrap}>
        <div style={card}>Loading invitation…</div>
      </div>
    );

  if (status === "error")
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Invitation problem</h2>
          <p style={{ color: "var(--text-muted)" }}>{errorMsg}</p>
          <Link to="/signin" style={{ color: "var(--primary-2)" }}>
            Go to sign in →
          </Link>
        </div>
      </div>
    );

  if (status === "done")
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Account activated 🎉</h2>
          <p style={{ color: "var(--text-muted)" }}>
            Redirecting you to sign in…
          </p>
        </div>
      </div>
    );

  if (status === "verify")
    return (
      <div style={wrap}>
        <div style={{ ...card, maxWidth: 480 }}>
          <h2 style={{ marginTop: 0 }}>Verify your phone via WhatsApp</h2>
          <WhatsAppVerifyStep
            token={token}
            waLink={verifyInfo?.waLink}
            botPhone={verifyInfo?.botPhone}
            verifyMessage={verifyInfo?.verifyMessage}
            phone={maskPhone(invitation?.phone)}
            expiresAt={verifyInfo?.expiresAt}
            onVerified={handleVerified}
          />
        </div>
      </div>
    );

  // status === 'ready' → step 1 form
  return (
    <div style={wrap}>
      <form style={card} onSubmit={handleStep1}>
        <h2 style={{ marginTop: 0 }}>Set up your account</h2>
        <p style={{ color: "var(--text-muted)", marginTop: -4 }}>
          You've been invited as a <strong>{invitation?.role}</strong>. Email:{" "}
          <strong>{invitation?.email}</strong>
        </p>
        <p
          style={{
            color: "var(--text-muted)",
            marginTop: -4,
            fontSize: 12.5,
          }}
        >
          After this step you'll verify your phone by tapping a one-time
          message in WhatsApp — no code to type.
        </p>

        {errorMsg && (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid var(--error)",
              color: "var(--error)",
              padding: 10,
              borderRadius: 8,
              fontSize: 13,
              margin: "10px 0",
            }}
          >
            {errorMsg}
          </div>
        )}

        <label
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--text-muted)",
          }}
        >
          Full name
        </label>
        <input
          style={inputStyle(fieldErrors.name)}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
        {fieldErrors.name && (
          <span style={{ fontSize: 11.5, color: "var(--error)" }}>
            {fieldErrors.name}
          </span>
        )}

        <div style={{ height: 12 }} />

        <label
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--text-muted)",
          }}
        >
          Password
        </label>
        <div style={{ position: "relative" }}>
          <input
            type={showPass ? "text" : "password"}
            style={{ ...inputStyle(fieldErrors.password), paddingRight: 40 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
          <button
            type="button"
            onClick={() => setShowPass((s) => !s)}
            aria-label={showPass ? "Hide password" : "Show password"}
            style={eyeBtnStyle}
          >
            <Icon name={showPass ? "eye-off" : "eye"} size={16} />
          </button>
        </div>
        {fieldErrors.password && (
          <span style={{ fontSize: 11.5, color: "var(--error)" }}>
            {fieldErrors.password}
          </span>
        )}
        <PasswordStrength password={password} />

        <div style={{ height: 12 }} />

        <label
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--text-muted)",
          }}
        >
          Confirm password
        </label>
        <div style={{ position: "relative" }}>
          <input
            type={showConfirm ? "text" : "password"}
            style={{ ...inputStyle(fieldErrors.confirm), paddingRight: 40 }}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((s) => !s)}
            aria-label={showConfirm ? "Hide password" : "Show password"}
            style={eyeBtnStyle}
          >
            <Icon name={showConfirm ? "eye-off" : "eye"} size={16} />
          </button>
        </div>
        {fieldErrors.confirm && (
          <span style={{ fontSize: 11.5, color: "var(--error)" }}>
            {fieldErrors.confirm}
          </span>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary"
          style={{ width: "100%", marginTop: 18, justifyContent: "center" }}
        >
          {submitting ? "Preparing…" : "Continue → Verify phone"}
        </button>
      </form>
    </div>
  );
}
